import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTerminal, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import protobuf from 'protobufjs';
import { Buffer } from 'buffer';
import { setCookieForTunnel } from '../../services/TunnelCookieAPI';
import './SSHTerminal.css';

const SSHTerminal = ({ sourceToken, region, thingName, onConnectionClose, onTokenExpired }) => {
    const terminalRef = useRef(null);
    const [terminal, setTerminal] = useState(null);
    const [socket, setSocket] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [streamId, setStreamId] = useState(null);
    const protoMessageRef = useRef(null);
    const [protoLoaded, setProtoLoaded] = useState(false);
    const connectionRetryRef = useRef(0);
    const maxRetries = 5;

    const safeResizeTerminal = (term) => {
        if (!term) return;

        try {
            if (typeof term.proposeGeometry === 'function') {
                const geometry = term.proposeGeometry();
                if (geometry && geometry.cols > 0 && geometry.rows > 0) {
                    term.resize(geometry.cols, geometry.rows);
                    return;
                }
            }

            const container = terminalRef.current;
            if (!container) return;

            const width = container.clientWidth || 0;
            const height = container.clientHeight || 0;
            if (width <= 0 || height <= 0) return;

            const approxCharWidth = 9;
            const approxRowHeight = 18;
            const cols = Math.max(40, Math.floor(width / approxCharWidth));
            const rows = Math.max(12, Math.floor(height / approxRowHeight));

            term.resize(cols, rows);
        } catch (error) {
                    console.error('Terminal resize failed:', error);
        }
    };

    useEffect(() => {
        if (!terminalRef.current) return;

        const Terminal = window.Terminal;
        if (!Terminal) {
            console.error('Terminal not loaded. Make sure xterm.min.js is loaded in index.html');
            return;
        }

        const term = new Terminal({
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            cursorBlink: true,
            theme: {
                background: '#1e1e1e',
                foreground: '#cccccc',
                cursor: '#ffffff',
                black: '#000000',
                red: '#cd3131',
                green: '#0dbc79',
                yellow: '#e5e510',
                blue: '#2472c8',
                magenta: '#bc3fbc',
                cyan: '#11a8cd',
                white: '#e5e5e5',
                brightBlack: '#666666',
                brightRed: '#f14c4c',
                brightGreen: '#23d18b',
                brightYellow: '#f5f543',
                brightBlue: '#3b8eea',
                brightMagenta: '#d670d6',
                brightCyan: '#29b8db',
                brightWhite: '#e5e5e5'
            },
            rows: 24,
            cols: 80,
            scrollback: 1000
        });

        term.open(terminalRef.current);

        setTimeout(() => {
            safeResizeTerminal(term);
        }, 100);

        term.writeln('AWS IoT Secure Tunneling Terminal');
        term.writeln('');

        if (!sourceToken) {
            term.writeln('\x1b[33mNo active session.\x1b[0m');
            term.writeln('Click "Start Session" to begin.');
        } else {
            term.writeln('Session active. Initializing connection...');
        }
        term.writeln('');

        setTerminal(term);

        const handleResize = () => {
            safeResizeTerminal(term);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (term && term.destroy) {
                term.destroy();
            }
        };
    }, [sourceToken]);

    useEffect(() => {
        const loadProto = async () => {
            try {
                const root = await protobuf.load('/ssh/format.proto');
                const Message = root.lookupType('com.amazonaws.iot.securedtunneling.Message');
                protoMessageRef.current = Message;
                setProtoLoaded(true);
                if (terminal) {
                    terminal.writeln('\x1b[32mProtocol definition loaded\x1b[0m');
                }
            } catch (error) {
                console.error('Failed to load protobuf:', error);
                if (terminal) {
                    terminal.writeln('\x1b[31mError: Failed to load protocol definition\x1b[0m');
                    terminal.writeln(`\x1b[31m${error.message}\x1b[0m`);
                }
            }
        };

        if (terminal) {
            loadProto();
        }
    }, [terminal]);

    useEffect(() => {
        if (!sourceToken || !region || !terminal || !protoLoaded) {
            return;
        }

        if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) {
            return;
        }

        connectToTunnel();

        return () => {
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };
    }, [sourceToken, region, terminal, protoLoaded]);

    const connectToTunnel = async () => {
        try {
            setConnectionStatus('connecting');
            terminal.writeln('Initializing secure tunnel connection...');
            terminal.writeln('');
            terminal.writeln('\x1b[33mWaiting for device to connect...\x1b[0m');

            await new Promise(resolve => setTimeout(resolve, 5000));
            terminal.writeln('\x1b[32m✓ Device connected\x1b[0m');

            terminal.writeln('\x1b[33mEstablishing secure connection...\x1b[0m');

            try {
                await setCookieForTunnel(sourceToken, region);
                terminal.writeln('\x1b[32m✓ Authentication successful\x1b[0m');
            } catch (error) {
                console.error('Failed to set cookie:', error);
                terminal.writeln('\x1b[31m✖ Cookie setup failed\x1b[0m');
                const isLikelyCorsCredentialIssue =
                    !error?.response &&
                    (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK');

                if (isLikelyCorsCredentialIssue) {
                    terminal.writeln('\x1b[33mLikely CORS credential issue on /tunnel/set-cookie.\x1b[0m');
                    terminal.writeln('\x1b[33mBackend must return exact Access-Control-Allow-Origin (not *).\x1b[0m');
                    terminal.writeln('\x1b[33mBackend must include Access-Control-Allow-Credentials: true.\x1b[0m');
                }
                terminal.writeln('');
                terminal.writeln('\x1b[90m────────────────────────────────────────\x1b[0m');
                terminal.writeln('\x1b[33mConnection could not be established.\x1b[0m');
                terminal.writeln('\x1b[33mPlease contact your system administrator.\x1b[0m');
                terminal.writeln('\x1b[90m────────────────────────────────────────\x1b[0m');
                setConnectionStatus('error');
                return;
            }

            const wsUrl = `wss://data.tunneling.iot.${region}.amazonaws.com:443/tunnel?local-proxy-mode=source`;
            const wsProtocols = ['aws.iot.securetunneling-3.0']

            terminal.writeln('\x1b[33mConnecting to AWS tunnel endpoint...\x1b[0m');

            const ws = new WebSocket(wsUrl, wsProtocols);
            ws.binaryType = 'arraybuffer';

            ws.onopen = () => handleWebSocketOpen(ws);
            ws.onclose = (e) => handleWebSocketClose(e);
            ws.onerror = (e) => handleWebSocketError(e);
            ws.onmessage = (e) => handleWebSocketMessage(e);

            ws.sendB64 = (data) => sendB64Message(ws, data);

            setSocket(ws);

        } catch (error) {
            console.error('Connection error:', error);
            terminal.writeln(`\x1b[31mConnection error: ${error.message}\x1b[0m`);
            setConnectionStatus('error');
        }
    };

    const handleWebSocketOpen = (ws) => {
        terminal.writeln('\x1b[32m✓ Connected successfully\x1b[0m');
        terminal.writeln('');
        setConnectionStatus('connected');
        connectionRetryRef.current = 0;
        initializeSSHClient(ws);
        startStream(ws);
    };

    const handleWebSocketClose = (e) => {
        terminal.writeln('');

        if (e.code === 1008) {
            terminal.writeln('\x1b[31m✖ Authentication failed\x1b[0m');
            terminal.writeln('\x1b[33mYour session has expired. Please start a new session.\x1b[0m');

            if (onTokenExpired) {
                onTokenExpired();
            }
        } else if (e.code === 1006) {
            connectionRetryRef.current += 1;

            if (connectionRetryRef.current < maxRetries) {
                terminal.writeln('\x1b[33m⚠ Device not ready yet. Retrying in 3 seconds...\x1b[0m');
                terminal.writeln(`\x1b[90mAttempt ${connectionRetryRef.current + 1}/${maxRetries}\x1b[0m`);

                setTimeout(() => {
                    terminal.writeln('');
                    terminal.writeln('\x1b[33mRetrying connection...\x1b[0m');
                    connectToTunnel();
                }, 3000);
            } else {
                terminal.writeln('\x1b[31m✖ Connection failed after multiple attempts\x1b[0m');
                terminal.writeln('\x1b[33mDevice might not be connected. Please try again.\x1b[0m');
                setConnectionStatus('error');
            }
        } else {
            terminal.writeln('\x1b[33mConnection closed\x1b[0m');
        }

        setConnectionStatus('disconnected');

        if (onConnectionClose) {
            onConnectionClose();
        }
    };

    const handleWebSocketError = (e) => {
        console.error('WebSocket error:', e);
        terminal.writeln('\x1b[31m✖ Connection error\x1b[0m');
        terminal.writeln('\x1b[33mAn error occurred while connecting to the device.\x1b[0m');
        setConnectionStatus('error');
    };

    const handleWebSocketMessage = (e) => {
        try {
            const Message = protoMessageRef.current;
            if (!Message) return;

            const decodedMessage = Message.decode(new Uint8Array(e.data).slice(2));
            const jsonMessage = Message.toObject(decodedMessage);
            const messageType = jsonMessage.type;

            switch (messageType) {
                case 1:
                    if (jsonMessage.payload) {
                        const payload = atob(jsonMessage.payload);
                        if (window.transport && window.transport.parceler) {
                            window.transport.parceler.handle(payload);
                        }
                    }
                    break;
                case 2:
                    setStreamId(jsonMessage.streamId);
                    break;
                case 3:
                    terminal.writeln('\x1b[33mStream reset by server\x1b[0m');
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error('Error handling WebSocket message:', error);
        }
    };

    const sendB64Message = (ws, data) => {
        try {
            const Message = protoMessageRef.current;
            if (!Message || !ws || ws.readyState !== WebSocket.OPEN) return;

            let dataType;
            let payloadBuffer = null;

            if (typeof data === 'number') {
                dataType = data;
            } else {
                dataType = 1;
                payloadBuffer = Buffer.from(btoa(data), 'base64');
            }

            const iotSecureObject = {
                type: dataType,
                streamId: streamId || 0,
                ignorable: true,
                payload: payloadBuffer
            };

            const encodedProtoMessage = Message.encode(iotSecureObject).finish();
            const msgLength = encodedProtoMessage.byteLength;
            const protoMsg = new Uint8Array(2 + msgLength);

            protoMsg.set(new Uint8Array([Math.floor(msgLength / 256), msgLength % 256]));
            protoMsg.set(encodedProtoMessage, 2);

            ws.send(protoMsg);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const startStream = (ws) => {
        const newStreamId = Math.floor(Math.random() * 10000);
        setStreamId(newStreamId);

        if (ws.sendB64) {
            ws.sendB64(2);
        }
    };

    const initializeSSHClient = (ws) => {
        if (window.SSHyClient) {
            const settings = new window.SSHyClient.settings();
            const transport = new window.SSHyClient.Transport(ws, settings);

            window.ws = ws;
            window.transport = transport;
            window.term = terminal;

            transport.settings.rsaCheckEnabled = false;

            terminal.focus();
            terminal.writeln('SSH client initialized');
            terminal.writeln('');
        } else {
            terminal.writeln('\x1b[31mError: SSH client not loaded\x1b[0m');
        }
    };

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'connected';
            case 'connecting':
                return 'connecting';
            case 'error':
                return 'error';
            default:
                return '';
        }
    };

    const getStatusText = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'Connected';
            case 'connecting':
                return 'Connecting...';
            case 'error':
                return 'Connection Error';
            case 'disconnected':
                return 'Disconnected';
            default:
                return 'Ready';
        }
    };

    if (!sourceToken) {
        return (
            <div className="ssh-terminal-container">
                <div className="ssh-terminal-header">
                    <div className="ssh-terminal-title">
                        <FontAwesomeIcon icon={faTerminal} className="me-2" />
                        SSH Terminal
                    </div>
                </div>
                <div className="ssh-terminal-body">
                    <div className="ssh-terminal-placeholder">
                        <div className="ssh-terminal-placeholder-icon">
                            <FontAwesomeIcon icon={faTerminal} />
                        </div>
                        <div className="ssh-terminal-placeholder-text">
                            Start a tunnel session to connect to the device
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const handleReconnect = () => {
        if (!terminal || !sourceToken) return;

        if (socket && socket.readyState === WebSocket.OPEN && connectionStatus === 'connected') {
            terminal.writeln('');
            terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
            terminal.writeln('\x1b[36mAlready connected to device\x1b[0m');
            terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
            terminal.writeln('');
            return;
        }

        terminal.writeln('');
        terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
        terminal.writeln('\x1b[36mManual reconnection initiated...\x1b[0m');
        terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
        terminal.writeln('');

        connectionRetryRef.current = 0;

        if (socket) {
            socket.close();
            setTimeout(() => {
                connectToTunnel();
            }, 500);
        } else {
            connectToTunnel();
        }
    };

    return (
        <div className="ssh-terminal-container">
            <div className="ssh-terminal-header">
                <div className="ssh-terminal-title">
                    <FontAwesomeIcon icon={faTerminal} className="me-2" />
                    SSH Terminal - {thingName}
                </div>
                <div className="ssh-terminal-actions">
                    {connectionStatus === 'error' || connectionStatus === 'disconnected' ? (
                        <button
                            type="button"
                            onClick={handleReconnect}
                            className="btn btn-sm btn-outline-primary"
                            title="Retry connection"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                            Reconnect
                        </button>
                    ) : null}
                    <div className="ssh-terminal-status">
                        <div className={`ssh-status-indicator ${getStatusColor()}`}></div>
                        <span className="ssh-status-text">{getStatusText()}</span>
                    </div>
                </div>
            </div>
            <div className="ssh-terminal-body">
                <div ref={terminalRef} style={{ height: '100%', width: '100%' }}></div>
            </div>
        </div>
    );
};

export default SSHTerminal;
